import { useEffect, useMemo, useState } from 'react'
import { Box, Container, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import ChipToggle from 'src/components/MUIBased/ChipToggle'
import LoadingScreen from 'src/components/MUIBased/LoadingScreen'
import { StandardPageHeader } from 'src/components/MUIBased/pageHeaders'
import { PaymentWarningBanner } from 'src/components/MUIBased/PaymentWarningBanner'
import { useServiceLines } from 'src/flows/AppointmentBooking/useServiceLines'
import { useOfferings } from 'src/hooks/useOfferings'
import { AppointmentProfile, ServiceLine } from 'src/types'
import OfferingDialog from 'src/pages/MUIBased/Appointments/components/OfferingDialog'
import OfferingsCardContainer from 'src/pages/MUIBased/Appointments/components/OfferingsCardContainer'
import { useAppointmentEvents } from 'src/pages/MUIBased/Appointments/hooks/useAppointmentEvents'
import AppointmentSearchPanel from 'src/pages/MUIBased/Appointments/components/AppointmentSearchPanel'
import { FilteredResults } from 'src/pages/MUIBased/Appointments/components/AppointmentSearchAutocomplete'

interface OfferingsByServiceLine {
    serviceLine: ServiceLine
    offerings: AppointmentProfile[]
}

const AppointmentsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const activeServiceLines =
        searchParams.get('serviceLines')?.split(',') || []
    const { offerings, isLoading: isOfferingsLoading } = useOfferings()
    const { serviceLines, isLoading: isServiceLinesLoading } = useServiceLines()
    const { trackServiceLineChange } = useAppointmentEvents()

    const [activeOffering, setActiveOffering] = useState<
        AppointmentProfile | undefined
    >()

    const [searchFilteredOfferings, setSearchFilteredOfferings] = useState<
        AppointmentProfile[] | undefined
    >()

    const isLoading =
        !offerings ||
        !serviceLines ||
        isOfferingsLoading ||
        isServiceLinesLoading

    // These values and their order should be pulled live from the back-end
    const defaultAppointmentGroupTags = [
        { name: 'not feeling well?', label: 'Not feeling well?' },
        { name: 'check-ups & prevention', label: 'Check-Ups & Prevention' },
        {
            name: 'hormones, cycles & sexual health',
            label: 'Hormones, Cycles & Sexual Health'
        },
        {
            name: 'chronic & specialized care',
            label: 'Chronic & Specialized Care'
        },
        { name: 'skin & aesthetics', label: 'Skin & Aesthetics' }
    ]

    // This is a grouping of appointment offerings by the default tags supplied,
    // currently, above.  These will serve as the default experience if there is
    // no set active service line.
    const defaultAppointmentGroups = useMemo(() => {
        if (!offerings) {
            return []
        }

        const defaultAppointmentGroup = {
            label: 'All appointments',
            offerings,
            limit: 3
        }

        return defaultAppointmentGroupTags
            .map(tag => ({
                label: tag.label,
                offerings: offerings?.filter(offering =>
                    offering.appointmentProfileTags
                        ?.map(apt => apt.name)
                        ?.includes(tag.name)
                ),
                limit: 3
            }))
            .concat(defaultAppointmentGroup)
    }, [offerings])

    // This is a grouping of offerings by the active service lines.  Once data has
    // been loaded successfully, it is computed and memoized when the active service
    // lines change.
    const aslFilteredOfferings = useMemo(() => {
        if (isLoading || !activeServiceLines.length) {
            return undefined
        }
        return activeServiceLines.reduce((cur, aslName: string) => {
            const serviceLine = serviceLines.find(sl => sl.name === aslName)
            if (!serviceLine) {
                return cur
            }

            cur.push({
                serviceLine,
                offerings: offerings.filter(offering =>
                    offering.marketingServiceLines?.some(
                        msl => msl.name === aslName
                    )
                )
            })
            return cur
        }, [] as OfferingsByServiceLine[])
    }, [activeServiceLines, isLoading, offerings])

    // This is a hack to prevent a weird bug that's because of having a feature-flagged component
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const activeOfferingParam = searchParams.get('appointment') ?? undefined

    useEffect(() => {
        if (activeOfferingParam) {
            const offering = offerings?.find(
                o => o.name === activeOfferingParam
            )
            if (offering) {
                setActiveOffering(offering)
            }
        }
    }, [activeOfferingParam, offerings])

    /**
     * NO HOOKS AFTER THIS POINT ================================================
     */

    if (isLoading) {
        return <LoadingScreen isLoading />
    }

    const toggleServiceLine = (serviceLine: string) => {
        // Gather the updated active service lines
        let updatedServiceLines: string[] = activeServiceLines
        if (updatedServiceLines.includes(serviceLine)) {
            // We're toggling off an active service line
            updatedServiceLines = updatedServiceLines.filter(
                sl => sl !== serviceLine
            )
        } else {
            // We're adding a new service line
            updatedServiceLines = [serviceLine, ...updatedServiceLines]
        }

        // Update the URL query params with the new service lines
        const params = new URLSearchParams(searchParams)
        if (!updatedServiceLines.length) {
            if (params.has('serviceLines')) {
                params.delete('serviceLines')
                setSearchParams(params)
            }
        } else {
            params.set('serviceLines', updatedServiceLines.join(','))
            setSearchParams(params)
        }

        trackServiceLineChange(updatedServiceLines)
    }

    const showSearchFilteredOfferings = !!searchFilteredOfferings
    const showASLFilteredOfferings =
        !showSearchFilteredOfferings && !!aslFilteredOfferings?.length
    const showDefaultOfferings =
        !showSearchFilteredOfferings && !showASLFilteredOfferings

    const returnResults = (results: FilteredResults | undefined) => {
        if (!results) {
            setSearchFilteredOfferings(undefined)
        } else {
            if (results.filteredOptions) {
                setSearchFilteredOfferings(results.filteredOptions)
            }
            if (results.selectedOption) {
                setActiveOffering(results.selectedOption)
            }
        }
    }

    return (
        <>
            <StandardPageHeader showMobileBackButton={false} />
            <PaymentWarningBanner />
            <AppointmentSearchPanel
                offerings={offerings}
                returnResults={returnResults}
            />
            <Container sx={{ maxWidth: '512px !important' }}>
                {!showSearchFilteredOfferings && (
                    <Box sx={{ pb: 3 }}>
                        {serviceLines.map(sl => {
                            const { name, label } = sl
                            return (
                                <ChipToggle
                                    key={name}
                                    label={label}
                                    selected={activeServiceLines.includes(name)}
                                    onClick={() => toggleServiceLine(name)}
                                />
                            )
                        })}
                    </Box>
                )}
                {showSearchFilteredOfferings && (
                    <OfferingsCardContainer
                        title={
                            searchFilteredOfferings.length > 0
                                ? searchFilteredOfferings?.length === 1
                                    ? '1 matching result'
                                    : `${searchFilteredOfferings.length} matching results`
                                : 'No matching results'
                        }
                        offerings={searchFilteredOfferings}
                        setActiveOffering={setActiveOffering}
                    />
                )}
                {showASLFilteredOfferings &&
                    aslFilteredOfferings.map(({ serviceLine, offerings }) => (
                        <OfferingsCardContainer
                            key={serviceLine.name}
                            title={`${serviceLine.label} appointments`}
                            offerings={offerings}
                            setActiveOffering={setActiveOffering}
                        />
                    ))}
                {showDefaultOfferings &&
                    defaultAppointmentGroups.map(group => (
                        <OfferingsCardContainer
                            key={group.label}
                            title={group.label}
                            offerings={group.offerings}
                            limit={group.limit}
                            setActiveOffering={setActiveOffering}
                        />
                    ))}
            </Container>
            <OfferingDialog
                isOpen={Boolean(activeOffering)}
                offering={activeOffering}
                onClose={() => setActiveOffering(undefined)}
            />
        </>
    )
}

export default AppointmentsPage
